import { type PropsWithChildren, useMemo } from 'react';
import { type DefaultTheme, ThemeProvider } from 'styled-components';
import { useWindowWidth } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { defaultTheme } from '../constants';
import { getBorderRadius } from '../utils';
import {
  defaultFormPopupStyles,
  FORM_POPUP_MEDIUM_BREAKPOINT,
  FORM_POPUP_SMALL_BREAKPOINT,
  mediumFormPopupStyles,
  smallFormPopupStyles
} from './constants';
import { getFormAppearanceSettings } from './utils';

export function FormThemeProvider({ children }: PropsWithChildren) {
  const {
    theme: { themeSettings }
  } = useSettings();
  const windowWidth = useWindowWidth();

  const { formBackgroundColor, cornerRadius: themeCornerRadius } =
    themeSettings;
  const { fieldHeadingFontStyle, ...popup } = useMemo(() => {
    const { cornerRadius = themeCornerRadius, ...baseFormPopupStyles } =
      (() => {
        if (windowWidth < FORM_POPUP_SMALL_BREAKPOINT) {
          return smallFormPopupStyles;
        }

        if (windowWidth < FORM_POPUP_MEDIUM_BREAKPOINT) {
          return mediumFormPopupStyles;
        }

        return defaultFormPopupStyles;
      })();

    return {
      ...baseFormPopupStyles,
      borderRadius: getBorderRadius(cornerRadius, {
        rounded: 12,
        circular: 24
      }),
      background: {
        type: 'color',
        color: formBackgroundColor
      }
    } as const;
  }, [windowWidth, formBackgroundColor, themeCornerRadius]);

  const form = useMemo(() => {
    const formAppearanceSettings = getFormAppearanceSettings(themeSettings);

    return {
      ...formAppearanceSettings,
      fieldHeadingFontStyle
    };
  }, [themeSettings, fieldHeadingFontStyle]);

  const theme: DefaultTheme = useMemo(() => {
    return {
      ...defaultTheme,
      popup,
      form
    };
  }, [popup, form]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
